import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { loginRequest, getSubscriptionStatus } from '../api/client';

export type Plan = 'free' | 'club' | 'pro';

interface AuthUser {
  token: string;
  clubId: string;
  email: string;
  name: string;
  clubName: string;
  badgeUrl: string;
  plan: Plan;
  role: string;
  demoMode: boolean;
  forcePasswordChange: boolean;
  /** Per-club feature flags. Snapshot from login response. */
  features: Record<string, boolean>;
  /** Set when the session was minted by SuperAdmin → "Open as them". The
   *  banner at the top of the admin uses this so the operator can't
   *  forget they're not logged in as themselves. */
  impersonating?: boolean;
  impersonatedBy?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  token: string | null;
  clubId: string | null;
  email: string | null;
  name: string | null;
  clubName: string | null;
  plan: Plan;
  role: string;
  demoMode: boolean;
  isSuperAdmin: boolean;
  isOwner: boolean;
  forcePasswordChange: boolean;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Per-club feature flags. `{}` if not yet loaded. Treat missing keys as false. */
  features: Record<string, boolean>;
  /** True when this session was minted via SuperAdmin → "Open as them". */
  impersonating: boolean;
  /** The operator's email when impersonating — surfaced in the banner. */
  impersonatedBy: string | null;
  login: (email: string, password: string) => Promise<{ forcePasswordChange: boolean }>;
  logout: () => void;
  refreshPlan: () => Promise<void>;
  clearForcePasswordChange: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return {};
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch {
    return {};
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload.exp) return false;
  const expMs = (payload.exp as number) * 1000;
  return Date.now() >= expMs;
}

function loadStoredAuth(): AuthUser | null {
  try {
    const token = localStorage.getItem('authToken');
    const userData = localStorage.getItem('authUser');
    if (!token || !userData) return null;
    if (isTokenExpired(token)) {
      localStorage.removeItem('authToken');
      localStorage.removeItem('authUser');
      return null;
    }
    return JSON.parse(userData) as AuthUser;
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // SuperAdmin → "Open as them" hands off the entire login bundle via
    // a base64'd URL fragment (#impersonate=…). When we land with one,
    // replace any existing session, then clear the fragment so the URL
    // bar reads cleanly + a refresh doesn't re-hydrate.
    const hash = window.location.hash;
    if (hash.startsWith('#impersonate=')) {
      try {
        const encoded = hash.slice('#impersonate='.length);
        const json = decodeURIComponent(escape(atob(encoded)));
        const bundle = JSON.parse(json) as AuthUser;
        if (bundle.token && bundle.clubId) {
          localStorage.setItem('authToken', bundle.token);
          localStorage.setItem('authUser', JSON.stringify(bundle));
          setUser(bundle);
          // Replace history entry so the fragment vanishes from the
          // URL bar and the token isn't sitting in the browser history.
          window.history.replaceState({}, '', window.location.pathname);
          setIsLoading(false);
          return;
        }
      } catch (err) {
        console.error('[Auth] impersonation handoff failed:', err);
      }
    }

    const stored = loadStoredAuth();
    if (stored) {
      setUser(stored);
    }
    setIsLoading(false);
  }, []);

  // Dynamic title + favicon based on logged-in club
  useEffect(() => {
    if (user?.clubName) {
      document.title = `${user.clubName} | Admin`;
    } else {
      document.title = 'GrassrootsFC Admin';
    }

    if (user?.badgeUrl) {
      let favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.rel = 'icon';
        document.head.appendChild(favicon);
      }
      favicon.type = 'image/png';
      favicon.href = user.badgeUrl;
    }
  }, [user?.clubName, user?.badgeUrl]);

  const login = async (email: string, password: string): Promise<{ forcePasswordChange: boolean }> => {
    const response = await loginRequest(email, password);
    const authUser: AuthUser = {
      token: response.token,
      clubId: response.clubId,
      email: response.email,
      name: response.name || '',
      clubName: response.clubName,
      badgeUrl: response.badgeUrl || '',
      plan: (response.plan as Plan) || 'free',
      role: response.role || 'admin',
      demoMode: response.demoMode === true,
      forcePasswordChange: response.forcePasswordChange === true,
      features: response.features || {},
    };
    localStorage.setItem('authToken', response.token);
    localStorage.setItem('authUser', JSON.stringify(authUser));
    setUser(authUser);
    return { forcePasswordChange: authUser.forcePasswordChange };
  };

  const logout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('authUser');
    setUser(null);
  };

  /** Called after a successful password change to clear the force-change flag in local state. */
  const clearForcePasswordChange = () => {
    if (!user) return;
    const updated: AuthUser = { ...user, forcePasswordChange: false };
    setUser(updated);
    localStorage.setItem('authUser', JSON.stringify(updated));
  };

  const refreshPlan = useCallback(async () => {
    if (!user) return;
    try {
      const sub = await getSubscriptionStatus();
      const newPlan = (sub.plan as Plan) || 'free';
      if (newPlan !== user.plan) {
        const updated = { ...user, plan: newPlan };
        setUser(updated);
        localStorage.setItem('authUser', JSON.stringify(updated));
      }
    } catch {
      // silent — not critical if it fails
    }
  }, [user]);

  const value: AuthContextType = {
    user,
    token: user?.token ?? null,
    clubId: user?.clubId ?? null,
    email: user?.email ?? null,
    name: user?.name ?? null,
    clubName: user?.clubName ?? null,
    plan: user?.plan ?? 'free',
    role: user?.role ?? 'admin',
    demoMode: user?.demoMode ?? false,
    isSuperAdmin: user?.role === 'superadmin',
    isOwner: user?.role === 'owner',
    forcePasswordChange: user?.forcePasswordChange ?? false,
    isAuthenticated: !!user && !isTokenExpired(user.token),
    isLoading,
    features: user?.features ?? {},
    impersonating: user?.impersonating === true,
    impersonatedBy: user?.impersonatedBy ?? null,
    login,
    logout,
    refreshPlan,
    clearForcePasswordChange,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
